import { REVIEW_SOURCE_DEFINITIONS, WIDGET_DEFINITIONS } from '@flexbyt/shared';
import { createFileRoute } from '@tanstack/react-router';
import { SourceGlyph, WidgetThumbnail } from '@flexbyt/widget-preview';
import { PageHero } from '../../components/ui/hero.js';
import { WhoScene } from '../../components/mascot/scenes.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../../components/ui/button.js';
import { Measure, Section } from '../../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../../components/ui/type.js';
import { WidgetShowcase } from '../../components/WidgetShowcase.js';
import { sourcePagePath } from '../../content/integrations.js';
import { INDUSTRIES, type IndustryContent } from '../../content/industries.js';
import { sampleSetForSource } from '../../content/sample-sets.js';
import {
    WIDGET_BUILDER_PATH,
    widgetContentByType,
    widgetPath,
} from '../../content/widgets.js';
import { track } from '../../lib/analytics.js';
import { breadcrumbSchema, jsonLd, seo } from '../../lib/seo.js';
import { startUrl } from '../../lib/site.js';

/**
 * Who ReviewsJet is for, organised by business rather than by mechanism.
 *
 * ===========================================================================
 * `who-its-for.index.tsx`, not `who-its-for.tsx`
 * ===========================================================================
 * The file name is load-bearing, and the difference is invisible until it
 * bites. With a sibling `who-its-for.$slug.tsx`, a file called
 * `who-its-for.tsx` is treated as a *layout* wrapping the child rather than as
 * the page at `/who-its-for` — so `/who-its-for/saas` server-rendered the SaaS
 * page correctly and then hydrated into this hub, because the client router
 * matched the layout and this component draws no `<Outlet />`.
 *
 * The symptom is the worst kind to debug: the right HTML arrives, the right
 * title is in the tab, `curl` says everything is fine, and the page silently
 * changes under the reader a second after it loads.
 * `integrations.index.tsx` alongside `integrations.$slug.index.tsx` is the same
 * convention, already in use here.
 *
 * ===========================================================================
 * The gap this fills
 * ===========================================================================
 * Every other page here is organised by how the product works: by the platform
 * the reviews come from (`/integrations/*`), by the layout they end up in
 * (`/widgets/*`), by the website builder they are pasted into (`/for/*`). That
 * is the right way to describe a product and the wrong way to meet somebody who
 * has not decided they want one.
 *
 * A restaurant owner does not search for "review aggregation widget". They
 * search "how do I show my Google reviews on my restaurant website" — a whole
 * sentence, of which each existing page answers a different half. This page
 * takes the sentence, lets somebody find themselves in a list, and then hands
 * them to the two pages that do the work.
 *
 * ===========================================================================
 * It is a hub, and each row now has a page behind it
 * ===========================================================================
 * This deliberately had no per-industry routes at first, on the grounds that
 * ten pages differing by a substituted noun are the thin programmatic pages the
 * brief rules out. The note said one would earn a page "because that trade has
 * enough specific content to fill one, and it will be written rather than
 * generated" — and that turned out to be true of all ten, for a reason that is
 * only obvious once the questions are written down.
 *
 * The trades do not differ much in *what the product does for them*. They
 * differ enormously in what they are worried about. A dentist asks whether
 * their regulator permits patient testimonials at all. An agency asks whether
 * there is white labelling. An Etsy seller asks why their marketplace needs a
 * browser extension when Google does not. Those are three unrelated
 * conversations, and no single page has room for thirty of them.
 *
 * So the pages exist, they are written per trade in `industries.ts`, and
 * `industries.test.ts` fails if any two of them share a headline, an intro, a
 * bullet or an answer. This page stays the hub: it is how somebody finds
 * themselves, and every row is a link into the page that talks to them.
 */
export const Route = createFileRoute('/who-its-for/')({
  head: () => {
    const { meta, links } = seo({
      title: 'Who ReviewsJet is for — reviews on your website, by trade',
      description:
        'Restaurants, hotels, trades, clinics, shops and agencies. Find your kind of business, see which platform your reviews are already on, and put them on your own website.',
      path: '/who-its-for',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Who it is for', path: '/who-its-for' },
            ])
          ),
        },
      ],
    };
  },
  component: WhoItsFor,
});

function WhoItsFor() {
  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Who it is for', path: '/who-its-for' },
        ]}
        art={<WhoScene />}
      >
        <Measure>
          <Eyebrow>Who it is for</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            If people already review you, this is{' '}
            <Mark>for your business</Mark>.
          </Heading>
          <Text size="lead" className="mt-6">
            ReviewsJet is not built for one industry. It is built for the
            situation almost every business is in — the reviews are on Google,
            Tripadvisor, Trustpilot or a marketplace, and the website is the one
            place they are not. Find yours below.
          </Text>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('who-its-for-hero')}
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: 'who-its-for-hero' })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="secondary" size="lg">
              Build a widget first
            </ButtonLink>
          </div>
        </Measure>
      </PageHero>

      {/* ------------------------------------------------------ The jump list */}
      {/*
        Links to the pages now, not anchors down this one.

        They were `#id` jumps when this hub was the whole feature. Each trade
        has a page of its own since — with the questions that trade actually
        asks, which differ far more than the rows below can show — so a chip
        that scrolled somebody four rows down would be withholding the thing
        they came for.

        Still not a filter. Filtering hides the nine other trades, and a reader
        who does not see themselves in the list should be able to scan it and
        pick the nearest neighbour rather than being shown an empty state.
      */}
      <Section size="tight" className="pt-8 pb-0">
        <nav aria-label="Kinds of business">
          <ul className="flex flex-wrap gap-2">
            {INDUSTRIES.map((industry) => (
              <li key={industry.id}>
                <SiteLink
                  to={`/who-its-for/${industry.id}`}
                  className="inline-block rounded-lg px-3 py-1.5 text-body-sm font-medium text-ink-700 ring-1 ring-ink-200 transition-colors duration-[var(--duration-fast)] hover:bg-ink-25 hover:text-ink-950 hover:ring-ink-300"
                >
                  {industry.name}
                </SiteLink>
              </li>
            ))}
          </ul>
        </nav>
      </Section>

      {/* ---------------------------------------------------------- The list */}
      <Section aria-labelledby="industries-heading">
        <h2 id="industries-heading" className="sr-only">
          Kinds of business ReviewsJet is for
        </h2>

        <ul className="space-y-4">
          {INDUSTRIES.map((industry) => (
            <li key={industry.id}>
              <IndustryCard industry={industry} />
            </li>
          ))}
        </ul>
      </Section>

      {/* ----------------------------------------------- What it looks like */}
      <Section tone="raised" aria-labelledby="example-heading">
        <Measure>
          <Heading level={2} id="example-heading">
            It is the same product either way.
          </Heading>
          <Text className="mt-4 max-w-prose">
            Whatever the trade, the work is the same three steps: connect the
            listing your reviews are already on, choose which ones are worth
            showing, and paste one line of code. This is a grid widget with
            nothing changed — the same renderer that will run on your site.
          </Text>
        </Measure>

        <div className="rj-canvas mt-10 rounded-2xl p-4 ring-1 ring-ink-150 sm:p-8">
          <WidgetShowcase
            type="grid"
            reviews={sampleSetForSource('google').reviews.slice(0, 6)}
          />
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <ButtonLink to={WIDGET_BUILDER_PATH} size="lg">
            Build a free review widget
          </ButtonLink>
          <ButtonLink to="/integrations" variant="secondary" size="lg">
            Where reviews come from
          </ButtonLink>
        </div>
      </Section>

      {/* ---------------------------------------------------------- Not here */}
      {/*
        The honest counterpart, and the reason this page is not just a list of
        flattery. Somebody who does not see their trade should be told plainly
        that the answer is still yes rather than being left to conclude the
        product is not for them — and somebody on Shopify should find that out
        here rather than after signing up.
      */}
      <Section aria-labelledby="missing-heading">
        <Measure>
          <Heading level={2} id="missing-heading" size="title">
            Not on the list?
          </Heading>
          <Text className="mt-4 max-w-prose">
            The list is the ten kinds of business we see most, not the ten it
            works for. ReviewsJet cares about two things: whether your reviews
            are on a platform it can read, and whether you can paste a line of
            HTML into your site. If both are true, the trade does not matter.
          </Text>
          <Text className="mt-4 max-w-prose">
            The one thing worth checking before you start is your website
            platform. There is a ReviewsJet app for{' '}
            <SiteLink to="/for/wix" className="underline decoration-ink-200 underline-offset-[3px] hover:text-ink-950">
              Wix
            </SiteLink>{' '}
            and{' '}
            <SiteLink to="/for/webflow" className="underline decoration-ink-200 underline-offset-[3px] hover:text-ink-950">
              Webflow
            </SiteLink>
            , a plugin for{' '}
            <SiteLink to="/for/wordpress" className="underline decoration-ink-200 underline-offset-[3px] hover:text-ink-950">
              WordPress
            </SiteLink>
            , and a snippet that works anywhere else. Shopify is the one we do
            not support.
          </Text>
        </Measure>
      </Section>

      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Your reviews are already written.
          </Heading>
          <Text className="mt-4 text-white/70">
            Whatever you do, somebody has already said something good about it
            somewhere. Putting that on your own website takes about ten minutes
            and costs nothing to try.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('who-its-for-close')}
              variant="inverse"
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: 'who-its-for-close' })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink to="/pricing" variant="inverse-ghost" size="lg">
              See pricing
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}

/**
 * One kind of business.
 *
 * ===========================================================================
 * A row, not a card in a grid
 * ===========================================================================
 * Three columns of cards is the shape this page wants to be and the shape that
 * makes it useless: at card width there is room for a heading and about fifteen
 * words, which is exactly enough to write the interchangeable sentence this
 * page exists to avoid. A full-width row has room for the two things that make
 * an entry worth reading — the specific obstacle, and what changes — plus the
 * source marks and the layout sketch that turn it into a signpost.
 *
 * The right-hand column is the signpost: which platforms these people's reviews
 * are already on, each linking to that source's page, and which layout suits
 * their site, linking to the layout's. Everything on this page that is depth
 * lives on the page it points at.
 */
function IndustryCard({ industry }: { industry: IndustryContent }) {
  const widget = widgetContentByType(industry.widget);
  const definition = WIDGET_DEFINITIONS[industry.widget];

  return (
    <article
      id={industry.id}
      /*
        `scroll-mt-24` so an anchor jump does not land the heading underneath
        the sticky site header — the one detail that makes a jump list feel
        broken rather than instant.
      */
      className="scroll-mt-24 overflow-hidden rounded-xl bg-white ring-1 ring-ink-150"
    >
      <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-14">
        <div>
          <h3 className="text-title text-ink-950">
            <SiteLink
              to={`/who-its-for/${industry.id}`}
              className="rounded-sm underline-offset-4 hover:underline"
            >
              {industry.name}
            </SiteLink>
          </h3>

          <p className="mt-4 max-w-prose text-body-sm text-ink-600">
            {industry.problem}
          </p>
          <p className="mt-3 flex gap-2.5 max-w-prose text-body-sm text-ink-800">
            <Arrow />
            {industry.outcome}
          </p>

          {/*
            The row is a summary; the page is the answer. Named explicitly
            rather than left to the heading link, because a heading that happens
            to be a link is not an invitation — and the questions on the other
            side of it are the reason this hub is worth having at all.
          */}
          <p className="mt-5">
            <SiteLink
              to={`/who-its-for/${industry.id}`}
              className="text-body-sm font-medium text-accent-700 underline decoration-accent-200 underline-offset-[3px] hover:decoration-accent-400"
            >
              Reviews for {industry.name} &rarr;
            </SiteLink>
          </p>
        </div>

        <div className="lg:border-l lg:border-ink-100 lg:pl-14">
          <p className="eyebrow text-ink-500">Your reviews are on</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {industry.sources.map((source) => (
              <li key={source}>
                <SiteLink
                  to={sourcePagePath(source)}
                  className="inline-flex items-center gap-2 rounded-lg bg-ink-25 px-2.5 py-1.5 text-caption font-medium text-ink-700 ring-1 ring-ink-150 transition-colors duration-[var(--duration-fast)] hover:bg-white hover:text-ink-950 hover:ring-ink-300"
                >
                  <SourceGlyph source={source} className="size-3.5 shrink-0" />
                  {REVIEW_SOURCE_DEFINITIONS[source].label}
                </SiteLink>
              </li>
            ))}
          </ul>

          <p className="eyebrow mt-6 text-ink-500">Layout that suits you</p>
          <div className="mt-3 flex items-start gap-3.5">
            <span className="w-16 shrink-0">
              <WidgetThumbnail type={industry.widget} compact />
            </span>
            <p className="text-body-sm text-ink-600">
              {widget ? (
                <SiteLink
                  to={widgetPath(widget)}
                  className="font-medium text-ink-950 underline decoration-ink-200 underline-offset-[3px]"
                >
                  {widget.seoName}
                </SiteLink>
              ) : (
                <span className="font-medium text-ink-950">
                  {definition.name}
                </span>
              )}
              {' — '}
              {industry.widgetReason}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * The mark before the outcome line.
 *
 * An arrow rather than a tick: a tick says "you get this", which is what the
 * feature lists elsewhere say. This line is the *consequence* of the sentence
 * above it, and an arrow is the punctuation for "and therefore".
 */
function Arrow() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="mt-1 size-3.5 shrink-0 text-accent-600"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2.5 8h11M9 3.5 13.5 8 9 12.5" />
    </svg>
  );
}
